import React, { type ReactNode, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { ConfluenceIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type SearchResultType as ConfluenceSearchResultType,
	getProductAndIDFromARI,
} from '@atlassian/search-client';
import { getConfluenceUrlWithAnalyticsQueryParams } from '@atlassian/search-common';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getConfluenceEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { getSearchResultType } from '../../../../../../common/ui/search-result-type';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useFeatures } from '../../../../../../controllers/store/bootstrap';

import type { searchConfluenceSpace_AtlassianSearchPage$key } from './__generated__/searchConfluenceSpace_AtlassianSearchPage.graphql';

export const isSearchConfluenceSpace = (
	node?: any,
): node is searchConfluenceSpace_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchConfluenceSpace';
};

export const SearchConfluenceSpace = ({
	node,
	position,
	cloudId,
	searchQuery: query,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchConfluenceSpace_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchConfluenceSpace_AtlassianSearchPage on SearchConfluenceSpace
			@argumentDefinitions(includeIconUrl: { type: "Boolean", defaultValue: false }) {
				__typename
				id
				description
				title
				type
				url
				iconUrl @include(if: $includeIconUrl)
				lastModified
			}
		`,
		node,
	);

	const [features] = useFeatures();

	const isSideBySideEnabled = features?.sideBySide?.enabled;

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		let containerId: string | null = null;
		return {
			id: data.id,
			containerId,
			spaceId: containerId,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			isSideBySideEnabled,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data, isSideBySideEnabled]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const metadataParts: [string, ReactNode][] = [
		['type', getSearchResultType(data.type as ConfluenceSearchResultType)],
		['last-modified', data.lastModified && <LastModified lastModified={data.lastModified} />],
	];

	let icon: React.JSX.Element;
	if (fg('rovo_search_confluence_icons_fps')) {
		icon = getConfluenceEntityIcon({
			iconUrl: data.iconUrl,
			type: data.type,
			size: 'medium',
		});
	} else {
		icon = <ConfluenceIcon size="medium" shouldUseNewLogoDesign />;
	}

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Confluence}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={getConfluenceUrlWithAnalyticsQueryParams({
				url: data.url,
				query,
				searchSessionId,
			})}
			title={enrichExcerpt(data.title)}
			icon={icon}
			headerMetadataParts={metadataParts}
			description={<DescriptionBody description={processExcerpt(data.description)} />}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};
